import { ActorSubclass } from '@icp-sdk/core/agent'
import { getTransferSolInstruction } from '@solana-program/system'
import {
  findAssociatedTokenPda,
  getCreateAssociatedTokenIdempotentInstructionAsync,
  getTransferCheckedInstruction,
} from '@solana-program/token-2022'
import {
  address,
  Address,
  appendTransactionMessageInstructions,
  compileTransaction,
  createNoopSigner,
  createSolanaRpc,
  createTransactionMessage,
  FullySignedTransaction,
  getBase58Decoder,
  IInstruction,
  JsonParsedTokenAccount,
  pipe,
  sendTransactionWithoutConfirmingFactory,
  setTransactionMessageFeePayerSigner,
  setTransactionMessageLifetimeUsingBlockhash,
  Signature,
} from '@solana/kit'
import { config } from '../appConfig'
import { _SERVICE as HelperActor } from '../idls/ethHelper/ethHelper.did'
import { Chain, SolanaToken } from '../interfaces/common'
import { SolSendParams, SplSendParams } from '../interfaces/walletActions'
import { logger } from '../logger'
import { base64ToArrayBuffer } from '../utils/arrayBuffer'
import { UnknownToken } from '../utils/tokenActions/getMetadata'

export const TOKEN_PROGRAM_2022 = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb'
export const TOKEN_PROGRAM = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'

const CLUSTER = config.fetchEnv('SOLANA_CLUSTER') || 'devnet'
const RPC_ENDPOINT = config.fetchEnv('SOLANA_RPC_ENDPOINT') || `https://api.${CLUSTER}.solana.com`

const MAX_POLL_COUNT = 40

class SolanaClient {
  rpc = createSolanaRpc(RPC_ENDPOINT)

  transactionExploreUrl(transactionId: string): string {
    return `https://explorer.solana.com/tx/${transactionId}?cluster=${CLUSTER}`
  }

  async getAllSplTokens(wallet: string, programId: string): Promise<UnknownToken[]> {
    const response = await this.rpc
      .getTokenAccountsByOwner(
        address(wallet),
        { programId: address(programId) },
        { encoding: 'jsonParsed' }
      )
      .send()

    return response.value.map(
      (accountInfo: { account: { data: { parsed: { info: JsonParsedTokenAccount } } } }) => ({
        chain: Chain.SOL,
        address: accountInfo.account.data.parsed.info.mint.toString(),
        balance: BigInt(accountInfo.account.data.parsed.info.tokenAmount.amount),
      })
    )
  }

  async sendSol(
    { from, to, amount }: SolSendParams,
    helperActor: ActorSubclass<HelperActor>
  ): Promise<void> {
    const fromAddress = address(from)

    const instruction = getTransferSolInstruction({
      source: createNoopSigner(fromAddress),
      destination: address(to),
      amount: Number(amount),
    })

    return this.#createAndSendTransaction(fromAddress, [instruction], helperActor)
  }

  async sendSpl(
    { from, to, amount, token }: SplSendParams,
    helperActor: ActorSubclass<HelperActor>
  ) {
    const fromAddress = address(from)
    const tokenAddress = address(token.address)
    const solanaProgramAddress = address(token.additionalData.solana_program_id)

    const createAssociatedTokenInstruction =
      await getCreateAssociatedTokenIdempotentInstructionAsync({
        mint: tokenAddress,
        owner: address(to),
        payer: createNoopSigner(fromAddress),
        tokenProgram: solanaProgramAddress,
      })

    const transferInstruction = getTransferCheckedInstruction(
      {
        source: await this.#getAssociatedTokenAccountAddress(from, token),
        destination: await this.#getAssociatedTokenAccountAddress(to, token),
        mint: tokenAddress,
        authority: fromAddress,
        amount: BigInt(amount),
        decimals: token.decimals,
      },
      {
        programAddress: solanaProgramAddress,
      }
    )

    return this.#createAndSendTransaction(
      fromAddress,
      [createAssociatedTokenInstruction, transferInstruction],
      helperActor
    )
  }

  async calculateAccountRent(solAddress: string): Promise<bigint> {
    const response = await this.rpc
      .getAccountInfo(address(solAddress), { encoding: 'base64' })
      .send()

    const data = response.value?.data[0] || ''
    const accountSize = base64ToArrayBuffer(data).byteLength

    return this.rpc.getMinimumBalanceForRentExemption(BigInt(accountSize)).send()
  }

  async hasAssociatedTokenAccount(owner: string, token: SolanaToken) {
    const associatedTokenAddress = await this.#getAssociatedTokenAccountAddress(owner, token)

    const response = await this.rpc
      .getAccountInfo(address(associatedTokenAddress), { encoding: 'base64' })
      .send()

    return !!response.value
  }

  async #getAssociatedTokenAccountAddress(owner: string, token: SplSendParams['token']) {
    const [associatedTokenAddress] = await findAssociatedTokenPda({
      mint: address(token.address),
      owner: address(owner),
      tokenProgram: address(token.additionalData.solana_program_id),
    })

    return associatedTokenAddress
  }

  async #createAndSendTransaction(
    signerAddress: Address,
    instructions: IInstruction[],
    helperActor: ActorSubclass<HelperActor>
  ) {
    const signer = createNoopSigner(signerAddress)

    const { value: latestBlockhash } = await this.rpc.getLatestBlockhash().send()

    const transactionMessage = pipe(
      createTransactionMessage({ version: 0 }),
      (m) => setTransactionMessageFeePayerSigner(signer, m),
      (m) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
      (m) => appendTransactionMessageInstructions(instructions, m)
    )

    const compiledTx = compileTransaction(transactionMessage)
    const signature = await helperActor.sign_sol([...compiledTx.messageBytes])

    if ('Ok' in signature) {
      const signedTx = {
        messageBytes: compiledTx.messageBytes,
        lifetimeConstraint: compiledTx.lifetimeConstraint,
        signatures: {
          [signerAddress]: signature.Ok,
        },
      } as unknown as FullySignedTransaction

      const sendTransaction = sendTransactionWithoutConfirmingFactory({ rpc: this.rpc })

      try {
        await sendTransaction(signedTx, { commitment: 'confirmed' })
        await this.#pollForFinalized(getBase58Decoder().decode(Uint8Array.from(signature.Ok)))
      } catch (e) {
        logger.error('Failed to send transaction', { originalError: e })
        throw this.#normalizeSendError(e)
      }
    } else {
      return Promise.reject(signature.Err)
    }
  }

  async #pollForFinalized(signature: string) {
    for (let retryCount = 0; retryCount < MAX_POLL_COUNT; retryCount += 1) {
      const response = await this.rpc
        .getSignatureStatuses([signature as Signature], { searchTransactionHistory: true })
        .send()

      const finalised = response.value[0]?.confirmationStatus === 'finalized'

      if (finalised) {
        return
      } else {
        await new Promise((r) => setTimeout(r, 500))
      }
    }

    throw 'Solana: unable to receive finalized transaction state'
  }

  #normalizeSendError(error: any) {
    if (error?.context?.__code === -32002) {
      return new Error(
        'The transaction seems to be invalid, make sure both accounts have enough SOL to cover rent fees'
      )
    } else {
      return new Error('Random error occured, please try again or contact support')
    }
  }
}

const solanaClient = new SolanaClient()

export { solanaClient }
